"use client";

import {
  type ChangeEvent,
  useDeferredValue,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  ChangeTable,
  IssueList,
  reportText,
  SCREEN_LIMIT,
} from "@/components/tools/identifier-tools/ris-report";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  checkRis,
  cleanRis,
  decodeRisBytes,
  parseRis,
  type RisEncoding,
  type RisFixOptions,
} from "@/lib/tool-kit/identifiers/ris-check";

/** English strings. A translated content file overrides these through `ui`. */
export const RIS_FILE_VALIDATOR_STRINGS = {
  inputLabel: "Your RIS file",
  inputPlaceholder: "TY  - JOUR\nAU  - Surname, Given\nTI  - Title\nER  - ",
  inputHint:
    "Paste the file's text or upload the .ris file. It is checked and cleaned in your browser — nothing is uploaded.",
  upload: "Upload a .ris file",
  uploadLabel: "Choose a RIS file to check",
  trySample: "Try a sample",
  clear: "Clear",
  sample:
    "TY  - JOUR\nAU  - Light, Janice\nAU  - McNaughton, David\nTI  - From basic to applied research to improve outcomes for individuals who require augmentative and alternative communication: Potential contributions of eye tracking research methods\nJO  - Augmentative and Alternative Communication\nPY  - 2014\nVL  - 30\nIS  - 2\nSP  - 99\nEP  - 105\nDO  - 10.3109/07434618.2014.906498\nER  - \n\nTY - JOUR\nAU  - Aspelmeyer, Markus\nTI  - Measured measurement\nT2  - Nature Physics\nPY  - 2009\nVL  - 5\nIS  - 1\nSP  - 11\nEP  - 12\nDO  - 10.1038/nphys1170\n\nTY  - JOUR\nAU  - Light, Janice\nAU  - McNaughton, David\nTI  - From basic to applied research to improve outcomes for individuals who require augmentative and alternative communication: Potential contributions of eye tracking research methods\nJO  - Augmentative and Alternative Communication\nPY  - 2014\nVL  - 30\nIS  - 2\nSP  - 99\nEP  - 105\nDO  - 10.3109/07434618.2014.906498\nER  - \n\nTY  - BOOK\nAU  - Cohen, Jacob\nTI  - Statistical power analysis for the behavioral sciences\nET  - 2nd\nPY  - 1988\nPU  - Lawrence Erlbaum Associates\nER  - \n",
  fileRead: "Read {name} as {encoding}.",
  encodingUtf8: "UTF-8",
  encodingUtf16:
    "UTF-16 — the cleaned file is saved as UTF-8, which every reference manager reads",
  encodingAnsi:
    "Windows-1252, because it is not valid UTF-8 — check the accented letters; the cleaned file is saved as UTF-8",
  fixesLabel: "Safe fixes for the cleaned file",
  fixNormalize:
    "Rewrite tag lines in the RIS form, remove stray byte-order marks and end every line with CR LF",
  fixAddEnd: "Close each record that has no ER line",
  fixMoveJo:
    "Move a full journal title from JO to T2, or drop a JO that repeats T2",
  fixDuplicates: "Remove records that repeat an earlier record exactly",
  emptyPrompt: "Paste or upload a RIS file to check it.",
  results: "Report",
  statRecords: "Records",
  statErrors: "Errors",
  statWarnings: "Warnings",
  statChanges: "Changes made",
  noProblems: "No problems found — the file follows the RIS format.",
  issuesHeading: "Problems found",
  changesHeading: "Changes in the cleaned file",
  noChanges: "No changes — the cleaned file is the same as yours.",
  showingFirst:
    "Showing the first {shown} of {total}. The downloaded report lists them all.",
  cleanedHeading: "Cleaned file",
  cleanedRecords:
    "one:{count} record in the cleaned file|other:{count} records in the cleaned file",
  cleanedPreview:
    "Showing the first {shown} of {total} lines. The download has the whole file.",
  downloadRis: "Download cleaned .ris",
  downloadReport: "Download report (.txt)",
  copyRis: "Copy cleaned RIS",
  copied: "Copied!",
  reportTitle: "RIS File Validator report",
  whereRecordLine: "Record {record}, line {line}",
  whereLine: "Line {line}",
  whereFile: "Whole file",
  severityError: "Error",
  severityWarning: "Warning",
  severityInfo: "Note",
  fixedTag: "Fixed in the cleaned file",
  colWhere: "Where",
  colChange: "Change",
  colBefore: "Before",
  colAfter: "After",
  fieldTitle: "title",
  fieldAuthor: "author",
  fieldCreator: "author or editor",
  fieldJournal: "journal",
  fieldBookTitle: "book title",
  fieldConference: "conference or proceedings",
  fieldYear: "year",
  fieldPublisher: "publisher",
  fieldUrl: "URL",
  issueNoRecords:
    "No RIS records found. A record starts with a type line such as “TY  - JOUR” and ends with “ER  - ”.",
  issueBomStart:
    "The file starts with a byte-order mark. Reference managers read it, and the cleaned file keeps it.",
  issueBomStray:
    "A byte-order mark in the middle of the file — usually where two exports were joined. It hides the tag that follows it from some importers.",
  issueMojibake:
    "“{sample}” looks like UTF-8 text that was read as Windows-1252. Export the file again as UTF-8, or correct the letters.",
  issueReplacementChar:
    "Contains �, the mark left where a character could not be decoded. The original letter is lost — retype it.",
  issueEndingsMixed:
    "Lines end in a mixture of CR LF, LF and CR. The specification asks for CR LF.",
  issueEndingsLf:
    "Lines end in LF. The specification asks for CR LF; most reference managers read either.",
  issueEndingsCr:
    "Lines end in CR alone, the old Mac convention. Some importers read the whole file as one line.",
  issueMalformedTag:
    "“{text}” is not in the RIS tag form: two characters, two spaces, a hyphen, a space.",
  issueUnknownTag:
    "{tag} is not a RIS tag. Zotero drops tags it does not know; other programs may keep the value as a note or ignore it.",
  issueOutsideRecord: "Text outside any record: “{text}”. Importers skip it.",
  issueMissingTy:
    "This record has no TY line. The type must come first; importers guess a type or skip the record.",
  issueMissingEr: "This record has no ER line to close it.",
  issueUnknownType:
    "“{type}” is not a RIS reference type, so importers will treat the record as generic.",
  issueUnknownTypeSuggestion:
    "“{type}” is not a RIS reference type — types are written in capitals, so this is probably {suggestion}.",
  issueMissingFields: "Missing for a {type} reference: {fields}.",
  issueMissingFieldsUntyped: "Missing: {fields}.",
  issueNoYear: "{tag} holds “{value}”, which has no four-digit year.",
  issueYearFormat:
    "PY holds “{value}”. PY is the four-digit year alone; the full date goes in DA as YYYY/MM/DD.",
  issueAuthorList:
    "Several names on one {tag} line. Give each person a line of their own.",
  issueErValue: "The ER line carries a value, which importers ignore.",
  issueJoFullTitle:
    "JO holds the full journal title “{value}”. Zotero reads JO as the abbreviation; the full title belongs in T2.",
  issueJoRepeatsTitle:
    "JO repeats the journal title. Zotero reads JO as the abbreviation, so the full title would be filed as one.",
  issueJoLooksFull:
    "JO holds “{value}”, which looks like a full title rather than an abbreviation of “{container}”. Check which belongs where.",
  issueDuplicateExact: "An exact duplicate of record {of}.",
  issueDuplicateDoi:
    "Same DOI as record {of} ({doi}) — probably the same work, exported twice.",
  issueDuplicateTitle:
    "Same title, year and first author as record {of} — probably the same work.",
  changeSpacing: "Tag line rewritten in the RIS form",
  changeBom: "Stray byte-order mark removed",
  changeLineEndings:
    "one:Line ending set to CR LF on {count} line|other:Line endings set to CR LF on {count} lines",
  changeEndAdded: "ER added to close record {record}",
  changeJoMoved: "JO changed to T2",
  changeJoDropped: "JO removed — it repeated T2",
  changeDuplicateDropped:
    "Record {record} removed (lines {line}–{to}): an exact duplicate of record {of}",
} as const;

const PREVIEW_LINES = 200;
const RIS_MIME = "application/x-research-info-systems;charset=utf-8";
const RE_EXTENSION = /\.[^.]+$/;
const RE_ANY_EOL = /\r\n|\r|\n/;

const ENCODING_KEY: Record<
  RisEncoding,
  keyof typeof RIS_FILE_VALIDATOR_STRINGS
> = {
  "utf-8": "encodingUtf8",
  "utf-16le": "encodingUtf16",
  "utf-16be": "encodingUtf16",
  "windows-1252": "encodingAnsi",
};

export default function RisFileValidatorWidget() {
  const strings = useToolUi(RIS_FILE_VALIDATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [file, setFile] = useState<{ name: string; encoding: RisEncoding }>();
  const [options, setOptions] = useState<RisFixOptions>({
    normalize: true,
    addEnd: true,
    moveJo: true,
    dropDuplicates: true,
  });
  const upload = useRef<HTMLInputElement>(null);
  const deferred = useDeferredValue(text);

  const parse = useMemo(() => parseRis(deferred), [deferred]);
  const issues = useMemo(() => checkRis(parse), [parse]);
  const cleaned = useMemo(
    () => cleanRis(parse, issues, options),
    [parse, issues, options]
  );

  function edit(value: string) {
    setText(value);
    setFile(undefined);
  }

  async function readFile(event: ChangeEvent<HTMLInputElement>) {
    const chosen = event.target.files?.[0];
    event.target.value = "";
    if (!chosen) {
      return;
    }
    const decoded = decodeRisBytes(new Uint8Array(await chosen.arrayBuffer()));
    setText(decoded.text);
    setFile({ name: chosen.name, encoding: decoded.encoding });
  }

  function toggle(key: keyof RisFixOptions) {
    return (checked: boolean) =>
      setOptions((current) => ({ ...current, [key]: checked }));
  }

  const hasText = deferred.trim() !== "";
  const errors = issues.filter((issue) => issue.severity === "error").length;
  const warnings = issues.filter(
    (issue) => issue.severity === "warning"
  ).length;
  const cleanedLines = cleaned.text.split(RE_ANY_EOL);
  if (cleanedLines.at(-1) === "") {
    cleanedLines.pop();
  }
  const baseName = file ? file.name.replace(RE_EXTENSION, "") : "references";

  return (
    <section className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="rfv-input"
        label={strings.inputLabel}
        onChange={edit}
        placeholder={strings.inputPlaceholder}
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => upload.current?.click()}
          type="button"
        >
          {strings.upload}
        </button>
        <label className="sr-only" htmlFor="rfv-file">
          {strings.uploadLabel}
        </label>
        <input
          accept=".ris,.txt,text/plain,application/x-research-info-systems"
          className="hidden"
          id="rfv-file"
          onChange={readFile}
          ref={upload}
          tabIndex={-1}
          type="file"
        />
        <button
          className={BUTTON_SECONDARY}
          onClick={() => edit(strings.sample)}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text === ""}
          onClick={() => edit("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {file ? (
        <p className={HINT}>
          {t(strings.fileRead, {
            name: file.name,
            encoding: strings[ENCODING_KEY[file.encoding]],
          })}
        </p>
      ) : null}
      <fieldset className="space-y-2">
        <legend className={LABEL}>{strings.fixesLabel}</legend>
        <Checkbox
          checked={options.normalize}
          label={strings.fixNormalize}
          onChange={toggle("normalize")}
        />
        <Checkbox
          checked={options.addEnd}
          label={strings.fixAddEnd}
          onChange={toggle("addEnd")}
        />
        <Checkbox
          checked={options.moveJo}
          label={strings.fixMoveJo}
          onChange={toggle("moveJo")}
        />
        <Checkbox
          checked={options.dropDuplicates}
          label={strings.fixDuplicates}
          onChange={toggle("dropDuplicates")}
        />
      </fieldset>

      <div aria-live="polite" className="space-y-6">
        {hasText ? null : <Notice>{strings.emptyPrompt}</Notice>}
        {hasText ? (
          <div className={`${RESULT_CARD} space-y-5`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className={WIDGET_H2}>{strings.results}</h2>
              <button
                className={BUTTON_SECONDARY}
                onClick={() =>
                  downloadText(
                    `${baseName}-report.txt`,
                    reportText(issues, cleaned.changes, strings, fmt, locale)
                  )
                }
                type="button"
              >
                {strings.downloadReport}
              </button>
            </div>
            <StatGrid>
              <StatCard
                label={strings.statRecords}
                value={fmt(parse.records.length)}
              />
              <StatCard
                emphasis={errors > 0}
                label={strings.statErrors}
                value={fmt(errors)}
              />
              <StatCard label={strings.statWarnings} value={fmt(warnings)} />
              <StatCard
                label={strings.statChanges}
                value={fmt(cleaned.changes.length)}
              />
            </StatGrid>

            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.issuesHeading}</h3>
              {issues.length === 0 ? (
                <Notice>{strings.noProblems}</Notice>
              ) : (
                <IssueList
                  issues={issues}
                  options={options}
                  strings={strings}
                />
              )}
              {issues.length > SCREEN_LIMIT ? (
                <p className={HINT}>
                  {t(strings.showingFirst, {
                    shown: fmt(SCREEN_LIMIT),
                    total: fmt(issues.length),
                  })}
                </p>
              ) : null}
            </div>

            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.changesHeading}</h3>
              {cleaned.changes.length === 0 ? (
                <Notice>{strings.noChanges}</Notice>
              ) : (
                <ChangeTable changes={cleaned.changes} strings={strings} />
              )}
              {cleaned.changes.length > SCREEN_LIMIT ? (
                <p className={HINT}>
                  {t(strings.showingFirst, {
                    shown: fmt(SCREEN_LIMIT),
                    total: fmt(cleaned.changes.length),
                  })}
                </p>
              ) : null}
            </div>

            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className={WIDGET_H3}>{strings.cleanedHeading}</h3>
                  <p className="text-muted-foreground text-sm">
                    {tPlural(strings.cleanedRecords, locale, cleaned.records, {
                      count: fmt(cleaned.records),
                    })}
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <CopyButton
                    copiedLabel={strings.copied}
                    label={strings.copyRis}
                    text={cleaned.text}
                  />
                  <button
                    className={BUTTON_SECONDARY}
                    onClick={() =>
                      downloadText(
                        `${baseName}-cleaned.ris`,
                        cleaned.text,
                        RIS_MIME
                      )
                    }
                    type="button"
                  >
                    {strings.downloadRis}
                  </button>
                </div>
              </div>
              <pre className="max-h-96 overflow-auto rounded-lg border border-border bg-card p-4 font-mono text-foreground text-xs leading-relaxed">
                {cleanedLines.slice(0, PREVIEW_LINES).join("\n")}
              </pre>
              {cleanedLines.length > PREVIEW_LINES ? (
                <p className={HINT}>
                  {t(strings.cleanedPreview, {
                    shown: fmt(PREVIEW_LINES),
                    total: fmt(cleanedLines.length),
                  })}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
